import { useMemo, useState } from "react";
import { History, MessageSquare, User } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  extractActivityChangeBullets,
  extractStatusChangeFromActivity,
  formatTimeAgo,
  isPropertyChangeEntry,
  type TicketActivityEntry,
} from "@sd/lib/ticketDetailActivity";
import type { TicketNoteEntry } from "@sd/lib/ticketNotesApi";

export type TicketDetailActivityTab = "history" | "notes" | "changes";

function ActivityLine({
  entry,
  index,
  resolveUserLabel,
}: {
  entry: TicketActivityEntry;
  index: number;
  resolveUserLabel: (raw: string | undefined | null) => string;
}) {
  const when = formatTimeAgo(entry.created_on);
  const author = resolveUserLabel(entry.created_by);
  const bullets = extractActivityChangeBullets(entry.activity_description);
  const isUpdate =
    /updat|edit|status|assign|priority/i.test(
      `${entry.activity_title} ${entry.activity_type}`
    ) || bullets.length > 0;
  const isCreate =
    /creat/i.test(`${entry.activity_title} ${entry.activity_type}`) ||
    entry.activity_type === "create";
  const actionLabel = isCreate
    ? "Created by"
    : isUpdate
      ? "Updated by"
      : "Activity by";

  return (
    <li className="border-b border-border/60 py-3 last:border-b-0 sm:py-4">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2 sm:gap-3">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground sm:h-8 sm:w-8">
            <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground">
              {actionLabel}{" "}
              <span className="font-semibold text-primary">{author}</span>
              {when ? (
                <span className="text-muted-foreground"> {when}</span>
              ) : null}
            </p>
            {bullets.length > 0 ? (
              <ul className="mt-1.5 list-disc space-y-1 pl-4 text-sm text-muted-foreground sm:mt-2 sm:pl-5">
                {bullets.map(line => {
                  const statusChange = extractStatusChangeFromActivity(
                    entry.activity_description
                  );
                  const inlineStatus = line.match(
                    /^Status changed from\s+(.+?)\s+to\s+(.+?)$/i
                  );
                  if (inlineStatus) {
                    return (
                      <li key={line}>
                        Status changed from{" "}
                        <em>{inlineStatus[1].replace(/_/g, " ")}</em> to{" "}
                        <em>{inlineStatus[2].replace(/_/g, " ")}</em>
                      </li>
                    );
                  }
                  if (
                    statusChange &&
                    line.startsWith("Status changed from")
                  ) {
                    return (
                      <li key={line}>
                        Status changed from{" "}
                        <em>{statusChange.prev.replace(/_/g, " ")}</em> to{" "}
                        <em>{statusChange.next.replace(/_/g, " ")}</em>
                      </li>
                    );
                  }
                  return <li key={line}>{line}</li>;
                })}
              </ul>
            ) : entry.activity_title.trim() &&
              !/updat|creat/i.test(entry.activity_title) ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {entry.activity_title.trim()}
              </p>
            ) : null}
          </div>
        </div>
        <span className="shrink-0 text-[10px] text-muted-foreground sm:text-xs">#{index + 1}</span>
      </div>
    </li>
  );
}

function NoteLine({
  note,
  index,
  resolveUserLabel,
}: {
  note: TicketNoteEntry;
  index: number;
  resolveUserLabel: (raw: string | undefined | null) => string;
}) {
  const when = formatTimeAgo(note.createdOn);
  const author = resolveUserLabel(note.createdBy);

  return (
    <li className="border-b border-border/60 py-3 last:border-b-0 sm:py-4">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2 sm:gap-3">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300 sm:h-8 sm:w-8">
            <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground">
              Updated by{" "}
              <span className="font-semibold text-primary">{author}</span>
              {when ? (
                <span className="text-muted-foreground"> {when}</span>
              ) : null}
            </p>
            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground sm:mt-2">
              {note.note}
            </p>
          </div>
        </div>
        <span className="shrink-0 text-[10px] text-muted-foreground sm:text-xs">#{index + 1}</span>
      </div>
    </li>
  );
}

export function TicketDetailActivitySection({
  activityEntries,
  activityPending,
  activityError = false,
  notes,
  notesPending,
  resolveUserLabel,
}: {
  activityEntries: TicketActivityEntry[];
  activityPending: boolean;
  activityError?: boolean;
  notes: TicketNoteEntry[];
  notesPending: boolean;
  notesError?: boolean;
  resolveUserLabel: (raw: string | undefined | null) => string;
}) {
  const [activityTab, setActivityTab] =
    useState<TicketDetailActivityTab>("history");

  const propertyEntries = useMemo(
    () => activityEntries.filter(isPropertyChangeEntry),
    [activityEntries]
  );

  const tabClass = (tab: TicketDetailActivityTab) =>
    cn(
      "px-3 py-2 text-xs font-medium sm:px-4 sm:text-sm",
      activityTab === tab
        ? "border-b-2 border-primary text-foreground"
        : "text-muted-foreground hover:text-foreground"
    );

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="flex flex-wrap border-b border-border/80">
        <button
          type="button"
          className={tabClass("history")}
          onClick={() => setActivityTab("history")}
        >
          History
        </button>
        <button
          type="button"
          className={tabClass("notes")}
          onClick={() => setActivityTab("notes")}
        >
          Notes
        </button>
        <button
          type="button"
          className={tabClass("changes")}
          onClick={() => setActivityTab("changes")}
        >
          Property changes
        </button>
      </div>

      <div className="px-4 py-2">
        {activityTab === "history" ? (
          activityPending ? (
            <p className="py-4 text-sm text-muted-foreground">Loading history…</p>
          ) : activityError ? (
            <p className="py-4 text-sm text-destructive">
              Could not load ticket history.
            </p>
          ) : activityEntries.length === 0 ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <History className="h-4 w-4" />
              No history entries for this ticket.
            </div>
          ) : (
            <ul>
              {activityEntries.map((entry, index) => (
                <ActivityLine
                  key={entry.id || `${entry.created_on}-${index}`}
                  entry={entry}
                  index={index}
                  resolveUserLabel={resolveUserLabel}
                />
              ))}
            </ul>
          )
        ) : null}

        {activityTab === "changes" ? (
          activityPending ? (
            <p className="py-4 text-sm text-muted-foreground">Loading history…</p>
          ) : activityError ? (
            <p className="py-4 text-sm text-destructive">
              Could not load ticket history.
            </p>
          ) : propertyEntries.length === 0 ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <History className="h-4 w-4" />
              No property changes recorded.
            </div>
          ) : (
            <ul>
              {propertyEntries.map((entry, index) => (
                <ActivityLine
                  key={entry.id || `${entry.created_on}-${index}`}
                  entry={entry}
                  index={index}
                  resolveUserLabel={resolveUserLabel}
                />
              ))}
            </ul>
          )
        ) : null}

        {activityTab === "notes" ? (
          notesPending ? (
            <p className="py-4 text-sm text-muted-foreground">Loading notes…</p>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
              <MessageSquare className="h-8 w-8 opacity-40" />
              <p className="font-medium text-foreground">No notes yet</p>
              <p className="max-w-sm text-xs leading-relaxed">
                Add a comment using the box above.
              </p>
            </div>
          ) : (
            <ul>
              {notes.map((note, index) => (
                <NoteLine
                  key={note.id}
                  note={note}
                  index={index}
                  resolveUserLabel={resolveUserLabel}
                />
              ))}
            </ul>
          )
        ) : null}
      </div>
    </section>
  );
}
